import { apiDownload, apiRequest, ApiClientError } from './client';

/** Kassenplätze und Kopplung von Geräten (Plan 06, E11). */

export interface StationDevice {
  registerId: string;
  mode: 'login' | 'name';
  /** Name des Kontos (Anmeldung) bzw. angegebener Name (Schnellzugang); nur für die Verwaltung des Basars. */
  name: string | null;
  deviceLabel: string | null;
  pairedAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
  uploadUntil: string | null;
  salesCount: number;
  missingCount: number;
}

export interface Station {
  id: string;
  label: string;
  quickAccess: 'off' | 'name';
  pinEnabled: boolean;
  /** Nur auf dem Bildschirm des Veranstalters, nie auf dem Aushang. */
  pin: string | null;
  link: string | null;
  pairingLockedUntil: string | null;
  status: 'free' | 'paired' | 'locked';
  /** Gelöschter Platz, dessen Geräte noch Verkäufe haben (für „Nur noch hochladen“ sichtbar). */
  deleted: boolean;
  createdAt: string;
  devices: StationDevice[];
}

export type PinAction = 'on' | 'off' | 'renew';

/** Verkauf einer Kasse zur Prüfung durch die Leitung. */
export interface RegisterSale {
  clientUuid: string;
  registerSeq: number | null;
  soldAt: string;
  totalCents: number;
  voided: boolean;
  lineCount: number;
  /** Grund des Stornos (U17d), `null` ohne Storno oder bei einer älteren Kasse ohne Angabe. */
  voidReason?: string | null;
  voidNote?: string | null;
}

export const stationsApi = {
  list: (eventId: string) => apiRequest<Station[]>('GET', `/events/${eventId}/register-stations`),
  create: (eventId: string, body: { label: string; quickAccess: 'off' | 'name'; pin: boolean }) =>
    apiRequest<Station>('POST', `/events/${eventId}/register-stations`, body),
  update: (
    eventId: string,
    stationId: string,
    body: { label?: string; quickAccess?: 'off' | 'name'; pin?: PinAction },
  ) => apiRequest<Station>('PATCH', `/events/${eventId}/register-stations/${stationId}`, body),
  remove: (eventId: string, stationId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/register-stations/${stationId}`),
  rotateToken: (eventId: string, stationId: string) =>
    apiRequest<Station>('POST', `/events/${eventId}/register-stations/${stationId}/rotate-token`),
  revokeDevice: (eventId: string, stationId: string, registerId: string) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/register-stations/${stationId}/devices/${registerId}/revoke`,
    ),
  allowUpload: (eventId: string, stationId: string, registerId: string) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/register-stations/${stationId}/devices/${registerId}/allow-upload`,
    ),
  registerSales: (eventId: string, registerId: string) =>
    apiRequest<RegisterSale[]>('GET', `/events/${eventId}/registers/${registerId}/sales`),
  voidAllSales: (eventId: string, registerId: string) =>
    apiRequest<{ voided: number; more: boolean }>(
      'POST',
      `/events/${eventId}/registers/${registerId}/void-all`,
    ),
  openCode: (eventId: string, rotate = false) =>
    apiRequest<{ link: string }>('POST', `/events/${eventId}/register-open-code`, { rotate }),
  cardsPdf: async (eventId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/register-station-cards`)).blob,
  cardPdf: async (eventId: string, stationId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/register-stations/${stationId}/card`)).blob,
  openPosterPdf: async (eventId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/register-open-code/poster`)).blob,
};

export interface InspectedStation {
  id: string;
  label: string;
  occupied: boolean;
  quickAccess: 'off' | 'name';
  pinRequired: boolean;
}

export interface PairingInspect {
  kind: 'station' | 'hub';
  eventId: string;
  eventTitle: string;
  organizationName: string;
  stations: InspectedStation[];
}

/** Kassenplatz zur Wahl in der Kasse: nur Name und Belegung, nie Code, PIN oder Link. */
export interface AvailableStation {
  id: string;
  label: string;
  occupied: boolean;
}

/** Antwort einer Kopplung: Kasse samt Sitzung (Anmeldung: Basar-Sitzung, Schnellzugang: Geräte-Token). */
export interface PairedRegister {
  id: string;
  eventId: string;
  stationId: string;
  label: string;
  createdAt: string;
  mode: 'login' | 'name';
  sessionToken: string;
  sessionExpiresAt: string;
}

/** Öffentliche Routen: kein Zugriffstoken, kein automatisches Erneuern (der Code steht im Body, nie in der Adresse). */
export const pairingApi = {
  inspect: (token: string) =>
    apiRequest<PairingInspect>('POST', '/register-pairing/inspect', { token }, { auth: false }),
  claimName: (body: {
    token: string;
    stationId?: string;
    name: string;
    pin?: string;
    clientDeviceId: string;
  }) => apiRequest<PairedRegister>('POST', '/register-pairing/claim-name', body, { auth: false }),
  claimLogin: (
    eventId: string,
    stationId: string,
    body: { token: string; clientDeviceId: string },
  ) =>
    apiRequest<PairedRegister>(
      'POST',
      `/events/${eventId}/register-stations/${stationId}/claim`,
      body,
    ),
  /** Plätze zur Wahl (nur Name und Belegung) für Personen mit Kassenrecht, ohne QR-Code. */
  availableStations: (eventId: string) =>
    apiRequest<AvailableStation[]>('GET', `/events/${eventId}/register-stations/available`),
  /** Gerät ohne QR-Code an einen freien, eingerichteten Platz koppeln (Kassenmodell E14-2). */
  openStation: (eventId: string, stationId: string, clientDeviceId: string) =>
    apiRequest<PairedRegister>('POST', `/events/${eventId}/register-stations/${stationId}/open`, {
      clientDeviceId,
    }),
  newStation: (eventId: string, body: { token: string; label: string; clientDeviceId: string }) =>
    apiRequest<PairedRegister>('POST', `/events/${eventId}/register-pairing/new-station`, body),
};

/** Fehlercodes der Kopplung für die Oberfläche. */
export const isPairingError = (error: unknown, code: string): boolean =>
  error instanceof ApiClientError && error.code === code;
